import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { useRushRates } from "@/components/Metric";

const QTYS = [25, 50, 100, 250, 500, 1000, 2500, 5000];

// Recomputes the module's quote at each standard quantity to show volume savings.
// Props: endpoint, makeBody(qty)->body, extract(data)->priced dict, signature (refetch key), unitLabel, tiers (qty list).
export default function VolumePricingTable({ endpoint, makeBody, extract, signature, unitLabel = "unit", className = "", tiers, onPick, activeQty, moduleKey }) {
  const qtys = Array.isArray(tiers) && tiers.length ? tiers : QTYS;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const rush = useRushRates();
  const retailTaxF = 1 + (rush.gst + rush.pst) / 100;
  const wsTaxF = 1 + rush.gst / 100;
  const vdOff = !!moduleKey && (rush.vdDisabled || []).includes(moduleKey);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all(
      qtys.map((q) =>
        api.post(endpoint, makeBody(q)).then((r) => ({ q, d: extract(r.data) })).catch(() => ({ q, d: null }))
      )
    ).then((out) => {
      if (!alive) return;
      setRows(out.map(({ q, d }) => ({
        qty: q,
        retail_total: d?.retail_total ?? d?.selling_price ?? d?.customer_price ?? null,
        retail_unit: d?.unit_price ?? null,
        wholesale_total: d?.wholesale_total ?? d?.wholesale_price ?? null,
        wholesale_unit: d?.wholesale_unit ?? null,
        disc: d?.volume_discount_pct ?? 0,
      })));
      setLoading(false);
    });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [signature]);

  const hasRetail = rows.some((r) => r.retail_total != null);
  const hasWs = rows.some((r) => r.wholesale_total != null);

  if (vdOff) return null;

  return (
    <div className={className} data-testid="volume-pricing-table">
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Volume Pricing {loading ? "· …" : ""}</div>
      <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
        <table className="w-full min-w-[600px] text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <th className="text-left px-2.5 py-2">Qty</th>
              <th className="text-right px-2.5 py-2">Discount</th>
              {hasRetail && <th className="text-right px-2.5 py-2">Retail / {unitLabel}</th>}
              {hasRetail && <th className="text-right px-2.5 py-2">Retail total</th>}
              {hasRetail && <th className="text-right px-2.5 py-2">Retail +tax</th>}
              {hasWs && <th className="text-right px-2.5 py-2">WS / {unitLabel}</th>}
              {hasWs && <th className="text-right px-2.5 py-2">WS total</th>}
              {hasWs && <th className="text-right px-2.5 py-2">WS +tax</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const active = onPick && activeQty != null && Number(activeQty) === Number(r.qty);
              return (
              <tr key={r.qty} data-testid="volume-row" onClick={onPick ? () => onPick(r.qty) : undefined}
                className={`border-b border-slate-100 last:border-0 transition-colors ${onPick ? "cursor-pointer hover:bg-slate-50" : ""} ${active ? "bg-sky-50" : ""}`}>
                <td className="px-2.5 py-2 num font-semibold">
                  {active && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2495D3] mr-2 align-middle" />}{r.qty}
                </td>
                <td className="px-2.5 py-2 text-right num">{r.disc > 0 ? <span className="text-emerald-600">−{r.disc}%</span> : <span className="text-slate-300">—</span>}</td>
                {hasRetail && <td className="px-2.5 py-2 text-right num text-slate-600">{r.retail_unit != null ? money(r.retail_unit) : "—"}</td>}
                {hasRetail && <td className="px-2.5 py-2 text-right num font-semibold text-[#2495D3]">{r.retail_total != null ? money(r.retail_total) : "—"}</td>}
                {hasRetail && <td className="px-2.5 py-2 text-right num text-[#2495D3]" data-testid="volume-retail-tax">{r.retail_total != null ? money(r.retail_total * retailTaxF) : "—"}</td>}
                {hasWs && <td className="px-2.5 py-2 text-right num text-slate-500">{r.wholesale_unit != null ? money(r.wholesale_unit) : "—"}</td>}
                {hasWs && <td className="px-2.5 py-2 text-right num text-slate-600">{r.wholesale_total != null ? money(r.wholesale_total) : "—"}</td>}
                {hasWs && <td className="px-2.5 py-2 text-right num text-slate-600" data-testid="volume-ws-tax">{r.wholesale_total != null ? money(r.wholesale_total * wsTaxF) : "—"}</td>}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Discounts are editable in Settings → Volume Discounts. +tax = Retail incl. GST {rush.gst}% + PST {rush.pst}%; WS incl. GST {rush.gst}% only.</p>
    </div>
  );
}
